import { flow, getEnv, types, Instance } from "mobx-state-tree";
import { endpoints } from "../api/ApiEndpoints";
import { snackbarNotificationsService } from "../notifications/SnackbarNotificationsService";

// Mirrors the backend ReviewStatus. Draft/InReview are set by saving; Approved comes from
// the approve endpoint only, and Stale is set by the server when the results are re-run.
export type ReviewStatus = "Draft" | "InReview" | "Approved" | "Stale";

/**
 * Which of a file's two approved detection lists a row belongs to.
 *
 * A file that ran both services has an OCR-side list (seeded from ocr.json) and an
 * analysis-side list (seeded from the analysis output). They are independent, and the
 * Comparison tab can put them head to head.
 */
export type ReviewSide = "Ocr" | "Analysis";

export interface ReviewedDetectionDto {
  side?: ReviewSide;
  stableKey?: string;
  source?: string;
  decision?: string;
  confidence?: number;
  note?: string;
  mainCode?: string;
  c1?: string;
  c2?: string;
  q1?: string;
  q2?: string;
  sa?: string;
  positionFrom?: string;
  positionTo?: string;
  distance: number;
  timeSeconds?: number;
  frameIndex?: number;
  /** A frame grabbed from the video, as a data URL. Uploaded to blob on approval. */
  capturedFrame?: string;
  /** File name of the stored frame, once it has been uploaded. */
  frameFileName?: string;
  /**
   * A link to that stored frame. Server-minted, display only.
   *
   * Without it a captured frame vanished from the card on save: approval clears `capturedFrame`
   * once the bytes are in storage, and a file NAME is not something an <img> can render.
   */
  frameUrl?: string;
  /**
   * The pipeline's own extracted frame for this observation, as a SAS URL — display only.
   *
   * Deliberately NOT `capturedFrame`: that field means "bytes to store", and a link cannot be
   * stored. Overloading it made every extracted damage export with no image at all, because the
   * upload step correctly refused to write a URL out as a PNG. The server re-finds this frame
   * from `frameIndex` at approval time; it has no matching property, so a value that rides along
   * in a save payload is simply ignored rather than stored.
   */
  machineFrameUrl?: string;
  /**
   * The reviewer's verdict on THIS detection: sign-off state, condition and risk. Stored here
   * rather than in ocr.json so the machine output stays untouched and the two can be compared.
   */
  detectionStatus?: "Draft" | "InReview" | "Approved" | "Stale";
  conditionRating?: "Excellent" | "Good" | "Fair" | "Poor" | "Critical";
  riskRating?: "Low" | "Medium" | "High" | "Critical";
}

export interface MainInformationDto {
  key: string;
  value: string;
}

export interface ReviewSavePayload {
  status: ReviewStatus;
  comment?: string;
  /**
   * Which approved list `detections` is. Reconciliation is scoped to this side server-side, so
   * the other side's rows are untouched. ALWAYS set it: omitting it makes the server fall back
   * to replacing the whole review, which deletes the other tab's approved list.
   */
  side?: ReviewSide;
  detections: ReviewedDetectionDto[];
  /**
   * The inspection header. OPTIONAL, and the distinction matters: the server REPLACES main
   * information with whatever a save carries, so an empty array deletes it while an absent field
   * leaves it alone (see ApplyMainInformation). A save that is not about the header — a detection
   * list, say — must omit it rather than send nothing in it, which is how a run without OCR kept
   * losing the one header it had.
   */
  mainInformation?: MainInformationDto[];
}

/**
 * The human sign-off on one file's AI/OCR results.
 *
 * Kept as its own service rather than folded into OcrService because it applies to every
 * kind of run — an analysis-only file has no OCR data but still gets reviewed and approved,
 * and approval is what unlocks the ISYBAU/DWA export.
 */
export const InspectionReviewServiceModel = types
  .model("InspectionReviewService", {
    fileId: types.maybe(types.number),
    status: types.optional(types.string, "Draft"),
    reviewedByUserId: types.maybe(types.number),
    reviewedAt: types.maybe(types.string),
    comment: types.optional(types.string, ""),

    // No grade here: condition and risk are the reviewer's own entry, per detection
    // (conditionRating / riskRating). The rule-driven grading engine was removed.

    detections: types.optional(types.frozen<ReviewedDetectionDto[]>(), []),
    mainInformation: types.optional(types.frozen<MainInformationDto[]>(), []),

    // Mirrored from the server so the button can be disabled with the same reason the
    // request would have returned, instead of letting the user hit a 400.
    canApprove: types.optional(types.boolean, false),
    approvalBlockedReason: types.optional(types.string, ""),
    // The same verdict for the VIDEO-ANALYSIS list on its own. Each process is signed off on the
    // strength of its own rows and produces its own documents, so the analysis tab has to ask
    // about the analysis list — the whole-review answer would let an OCR row unblock a sign-off
    // that then generates analysis documents from nothing.
    analysisCanApprove: types.optional(types.boolean, false),
    analysisApprovalBlockedReason: types.optional(types.string, ""),
    // Each process's own sign-off state. `status` above is the file as a whole; these two are
    // what a tab reads about ITSELF, because the two lists are approved separately and a file is
    // legitimately "video analysis approved, Inspection Insights still in review".
    analysisStatus: types.optional(types.string, "Draft"),
    ocrStatus: types.optional(types.string, "Draft"),
    videoAnalysisExportAvailable: types.optional(types.boolean, false),
    ocrReviewedExportAvailable: types.optional(types.boolean, false),

    // Which documents the server actually holds. The two flags above only say "approved, and
    // this service ran" — they cannot say that ISYBAU was withheld because the inspection has no
    // manhole ids, which is why every format was offered and the missing one 404'd with a
    // message blaming approval.
    approvedOcrIsybauAvailable: types.optional(types.boolean, false),
    approvedOcrDwaAvailable: types.optional(types.boolean, false),
    approvedOcrResultsAvailable: types.optional(types.boolean, false),
    videoAnalysisIsybauAvailable: types.optional(types.boolean, false),
    videoAnalysisDwaAvailable: types.optional(types.boolean, false),

    isLoading: types.optional(types.boolean, false),
    isSaving: types.optional(types.boolean, false),
  })
  .views((self) => ({
    /**
     * Whether ONE process is signed off.
     *
     * The whole-file `isApproved` is the wrong question for a tab: with per-side status an
     * approved analysis list would otherwise report the OCR list as approved too, and the OCR
     * exports would unlock on a sign-off that was never about them.
     */
    isApprovedFor(side: ReviewSide) {
      return (side === "Analysis" ? self.analysisStatus : self.ocrStatus) === "Approved";
    },
    get isApproved() {
      return self.status === "Approved";
    },
    get isStale() {
      return self.status === "Stale";
    },
    get hasReview() {
      return self.detections.length > 0 || self.mainInformation.length > 0;
    },
    /**
     * The two approved lists, split by side. Rows saved before the side column existed carry no
     * `side`, so they fall back to the OCR list — matching the server-side backfill, which only
     * moved model-proposed (AI) rows across.
     */
    get ocrDetections(): ReviewedDetectionDto[] {
      return (self.detections ?? []).filter((d) => (d.side ?? "Ocr") === "Ocr");
    },
    get analysisDetections(): ReviewedDetectionDto[] {
      return (self.detections ?? []).filter((d) => d.side === "Analysis");
    },
    detectionsForSide(side: ReviewSide): ReviewedDetectionDto[] {
      return side === "Analysis"
        ? (self.detections ?? []).filter((d) => d.side === "Analysis")
        : (self.detections ?? []).filter((d) => (d.side ?? "Ocr") === "Ocr");
    },
  }))
  .actions((self) => {
    const applyResponse = (response: any) => {
      self.status = response?.status ?? "Draft";
      self.reviewedByUserId = response?.reviewedByUserId ?? undefined;
      self.reviewedAt = response?.reviewedAt ?? undefined;
      self.comment = response?.comment ?? "";
      self.detections = response?.detections ?? [];
      self.mainInformation = response?.mainInformation ?? [];
      self.canApprove = response?.canApprove ?? false;
      self.approvalBlockedReason = response?.approvalBlockedReason ?? "";
      // An older API sends neither, so the whole-review answer stands in.
      self.analysisCanApprove = response?.analysisCanApprove ?? self.canApprove;
      self.analysisApprovalBlockedReason =
        response?.analysisApprovalBlockedReason ?? self.approvalBlockedReason;
      // An older API sends neither, so the file-level status stands in for both.
      self.analysisStatus = response?.analysisStatus ?? self.status;
      self.ocrStatus = response?.ocrStatus ?? self.status;
      self.videoAnalysisExportAvailable = response?.videoAnalysisExportAvailable ?? false;
      self.ocrReviewedExportAvailable = response?.ocrReviewedExportAvailable ?? false;

      // Absent means "this server does not report per-document availability", NOT "no documents".
      // The distinction matters: defaulting the per-document flags to false against a server that
      // predates them made every XML entry disappear from a file that plainly had its documents
      // on disk. `??` keeps an explicit false from the newer server while letting an older one
      // fall back to the coarse flag it does send.
      self.approvedOcrIsybauAvailable =
        response?.approvedOcrIsybauAvailable ?? self.ocrReviewedExportAvailable;
      self.approvedOcrDwaAvailable =
        response?.approvedOcrDwaAvailable ?? self.ocrReviewedExportAvailable;
      self.approvedOcrResultsAvailable =
        response?.approvedOcrResultsAvailable ?? self.ocrReviewedExportAvailable;
      self.videoAnalysisIsybauAvailable =
        response?.videoAnalysisIsybauAvailable ?? self.videoAnalysisExportAvailable;
      self.videoAnalysisDwaAvailable =
        response?.videoAnalysisDwaAvailable ?? self.videoAnalysisExportAvailable;
    };

    // Clears the loaded review back to "not reviewed" locally, without touching the server.
    // Deliberately a closure rather than an action: setFileId is its only caller, and reaching a
    // sibling action through `this` both trips TS7022 and — while the async `reset` action below
    // shared this name — silently resolved to that one, so merely changing file issued a DELETE
    // and destroyed the review.
    const clearLoadedReview = () => {
      self.status = "Draft";
      self.reviewedByUserId = undefined;
      self.reviewedAt = undefined;
      self.comment = "";
      self.detections = [];
      self.mainInformation = [];
      self.canApprove = false;
      self.approvalBlockedReason = "";
      // Per-side verdicts and statuses. Omitting them here meant a file switch kept the previous
      // file's answer until the new review landed -- long enough for the Save button's enabled
      // state and the "why can this not be approved" notice to describe the wrong inspection.
      self.analysisCanApprove = false;
      self.analysisApprovalBlockedReason = "";
      self.analysisStatus = "Draft";
      self.ocrStatus = "Draft";
      self.videoAnalysisExportAvailable = false;
      self.ocrReviewedExportAvailable = false;
      self.approvedOcrIsybauAvailable = false;
      self.approvedOcrDwaAvailable = false;
      self.approvedOcrResultsAvailable = false;
      self.videoAnalysisIsybauAvailable = false;
      self.videoAnalysisDwaAvailable = false;
    };

    return {
      setFileId(fileId: number | undefined) {
        if (self.fileId === fileId) return;
        self.fileId = fileId;
        clearLoadedReview();
      },

      load: flow(function* () {
        if (!self.fileId) return;

        const requestedFileId = self.fileId;
        self.isLoading = true;
        try {
          const endpoint = endpoints.get("media_file_inspection_review");
          endpoint.routeParams = [requestedFileId];
          const response = yield getEnv(self).api.get(endpoint);

          // Reviews load alongside slower va-data calls, so a fast file switch can land an
          // earlier response after the later one. Discard it rather than showing the
          // previous file's review.
          if (self.fileId !== requestedFileId) return;

          applyResponse(response);
        } catch (error) {
          // Never let this reject. Every caller fires it as a background refresh
          // (`void inspectionReview.load()`), so a rejection escapes as an unhandled promise
          // rejection and surfaces as an uncaught runtime error overlay in dev.
          //
          // State is deliberately left alone: setFileId() has already reset it when the file
          // changed, so a new file degrades to "not reviewed", while a failed REFRESH of the
          // file already open keeps the last known good review rather than making the
          // reviewer's approval appear to vanish.
        } finally {
          if (self.fileId === requestedFileId) self.isLoading = false;
        }
      }),

      /**
       * Persist one side of the review. Reports failure through a snackbar and a `false`
       * return rather than rejecting: it is called from a dozen component handlers that only
       * `await` it, so a rejection would escape every one of them as an unhandled promise
       * rejection instead of telling the reviewer their edit did not stick.
       */
      save: flow(function* (payload: ReviewSavePayload) {
        if (!self.fileId) return false;

        self.isSaving = true;
        try {
          const endpoint = endpoints.get("media_file_inspection_review");
          endpoint.routeParams = [self.fileId];
          yield getEnv(self).api.put(endpoint, payload);
          yield (self as any).load();
          return true;
        } catch (error: any) {
          const reason = error?.response?.data ?? error?.message;
          snackbarNotificationsService.showError(
            typeof reason === "string" && reason.length > 0 ? reason : "general.error"
          );
          return false;
        } finally {
          self.isSaving = false;
        }
      }),

      /**
       * Throw the review away: the server deletes the approved detection files for both sides,
       * along with the reviewer's main-information changes and the generated standards
       * documents. The AI output is untouched, so the file keeps its results and simply reads
       * as never reviewed — the graph and detection list go empty until something is approved
       * again. Reloads afterwards so the page reflects it without a refresh.
       */
      reset: flow(function* () {
        if (!self.fileId) return false;

        self.isSaving = true;
        try {
          const endpoint = endpoints.get("media_file_inspection_review");
          endpoint.routeParams = [self.fileId];
          yield getEnv(self).api.delete(endpoint);
          yield (self as any).load();
          return true;
        } catch (error: any) {
          const reason = error?.response?.data ?? error?.message;
          snackbarNotificationsService.showError(
            typeof reason === "string" && reason.length > 0 ? reason : "general.error"
          );
          return false;
        } finally {
          self.isSaving = false;
        }
      }),

      /**
       * Sign the review off. Generating the standards documents happens server-side as part
       * of approval, so a success here means the export is ready (or that the server has
       * logged why a document could not be produced).
       */
      /**
       * Sign one process off, which is what makes the server write ITS documents.
       *
       * The side is passed through: the analysis list produces the VA ISYBAU/DWA pair and the
       * OCR list produces the approved-OCR pair, each from its own rows. Without it, saving one
       * list restamped the other's deliverables — and could sign a list off on the strength of
       * rows that belong to the other process. Omitted, the whole review is approved, which is
       * what callers that predate the split mean.
       */
      approve: flow(function* (side?: ReviewSide) {
        if (!self.fileId) return false;

        self.isSaving = true;
        try {
          const endpoint = endpoints.get("media_file_inspection_review_approve");
          endpoint.routeParams = [self.fileId];
          endpoint.queryParams = side ? `side=${side}` : "";
          yield getEnv(self).api.post(endpoint, {});
          yield (self as any).load();
          return true;
        } catch (error: any) {
          // The server refuses with a reason (nothing accepted, or no main information on a
          // run without OCR). Surface it verbatim — it tells the reviewer what to fix.
          const reason = error?.response?.data ?? error?.message;
          snackbarNotificationsService.showError(
            typeof reason === "string" && reason.length > 0 ? reason : "general.error"
          );
          return false;
        } finally {
          self.isSaving = false;
        }
      }),
    };
  });

export type InspectionReviewService = Instance<typeof InspectionReviewServiceModel>;
